import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { useI18n } from '../../i18n';
import DatePickerInput from '../Common/DatePickerInput';
import TimePickerInput from '../Common/TimePickerInput';
import MemberAvailabilityCalendar from './MemberAvailabilityCalendar';
import MemberAvailabilityCalendarSkeleton from './MemberAvailabilityCalendarSkeleton';
import AvailabilityService from '../../services/AvailabilityService';
import meetingService from '../../services/meetingService';
import { notify } from '../CustomToast';
import '../Projects/ProjectComposer.css';
import './ProposeNewTimeModal.css';
import calendarDotsIcon from '../../assets/CalendarDots.svg';
import clockIcon from '../../assets/Clock.svg';
import arrowRightIcon from '../../assets/ArrowRight.svg';

const ProposeNewTimeModal = ({ isOpen, onClose, meeting, onSuccess }) => {
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [availabilityMembers, setAvailabilityMembers] = useState([]);
  const [availabilityAnchorDate, setAvailabilityAnchorDate] = useState(null);
  const [formData, setFormData] = useState({
    start_date: null,
    start_time: null,
    end_date: null,
    end_time: null,
    message: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen || !meeting) {
      return;
    }

    const startAt = meeting?.start_at ? new Date(meeting.start_at) : null;
    const endAt = meeting?.end_at ? new Date(meeting.end_at) : null;

    setFormData({
      start_date: startAt,
      start_time: startAt,
      end_date: endAt,
      end_time: endAt,
      message: '',
    });
    setErrors({});
  }, [isOpen, meeting]);

  useEffect(() => {
    if (!isOpen || !meeting?.id) {
      return;
    }

    const anchorDate = formData.start_date || (meeting?.start_at ? new Date(meeting.start_at) : null);

    setIsLoadingAvailability(true);
    AvailabilityService.fetchMeetingAvailability(meeting.id, anchorDate)
      .then((response) => {
        const data = response?.data || {};
        setAvailabilityMembers(data.members || []);
        setAvailabilityAnchorDate(data.anchor_date || null);
      })
      .catch((error) => {
        console.error('Error fetching availability:', error);
        setAvailabilityMembers([]);
        setAvailabilityAnchorDate(null);
      })
      .finally(() => {
        setIsLoadingAvailability(false);
      });
  }, [formData.start_date, isOpen, meeting?.id, meeting?.start_at]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleStartDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      start_date: date,
      end_date: prev.end_date || date,
    }));
    if (errors.start_date) setErrors((prev) => ({ ...prev, start_date: null }));
  };

  const handleStartTimeChange = (time) => {
    setFormData((prev) => ({ ...prev, start_time: time }));
    if (errors.start_time) setErrors((prev) => ({ ...prev, start_time: null }));
  };

  const handleEndDateChange = (date) => {
    setFormData((prev) => ({ ...prev, end_date: date }));
    if (errors.end_date) setErrors((prev) => ({ ...prev, end_date: null }));
  };

  const handleEndTimeChange = (time) => {
    setFormData((prev) => ({ ...prev, end_time: time }));
    if (errors.end_time) setErrors((prev) => ({ ...prev, end_time: null }));
  };

  const handleMessageChange = (e) => {
    setFormData((prev) => ({ ...prev, message: e.target.value }));
  };

  const formatMeetingDate = () => {
    if (!meeting?.start_at) return '';
    const start = new Date(meeting.start_at);

    return start.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatMeetingStartTime = () => {
    if (!meeting?.start_at) return '';
    const start = new Date(meeting.start_at);

    return start.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatMeetingEndTime = () => {
    if (!meeting?.end_at) return '';
    const end = new Date(meeting.end_at);

    return end.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleSubmit = async () => {
    // Validation
    const newErrors = {};

    if (!formData.start_date) {
      newErrors.start_date = [t('validation.required') || 'Start date is required'];
    }
    if (!formData.start_time) {
      newErrors.start_time = [t('validation.required') || 'Start time is required'];
    }
    if (!formData.end_date) {
      newErrors.end_date = [t('validation.required') || 'End date is required'];
    }
    if (!formData.end_time) {
      newErrors.end_time = [t('validation.required') || 'End time is required'];
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Combine date and time into ISO8601 format
    const formatDateTime = (dateObj, timeObj) => {
      if (!dateObj || !timeObj) return null;
      const date = new Date(dateObj);
      const time = new Date(timeObj);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(time.getHours()).padStart(2, '0');
      const minute = String(time.getMinutes()).padStart(2, '0');
      const second = '00';

      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    };

    const proposed_start_at = formatDateTime(formData.start_date, formData.start_time);
    const proposed_end_at = formatDateTime(formData.end_date, formData.end_time);

    setIsSubmitting(true);
    try {
      const payload = {
        proposed_start_at,
        proposed_end_at,
        message: formData.message || null,
      };

      const response = await meetingService.proposeTime(meeting.id, payload);

      if (response.status === true) {
        notify(
          'success',
          t('pages.meetings.proposal_submitted') || 'Proposal submitted successfully'
        );
        onSuccess?.(response?.data?.proposal);
        onClose();
      } else {
        const errorMsg = response.message || t('common.messages.error_submitting_proposal') || 'Error submitting proposal';
        notify('error', errorMsg);
      }
    } catch (error) {
      const errorErrors = error?.errors || {};

      if (error?.status === 422 && Object.keys(errorErrors).length > 0) {
        setErrors(errorErrors);
        notify(
          'error',
          error?.message || t('common.messages.validation_error') || 'Validation error'
        );
      } else {
        const message =
          error?.message ||
          t('common.messages.error_submitting_proposal') ||
          'Error submitting proposal';
        notify('error', message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="project-composer-overlay pnt-overlay"
      onClick={handleBackdropClick}
    >
      <div className="project-composer-modal pnt-modal-container">
        {/* Header */}
        <div className="project-composer-header">
          <div className="pnt-header-content">
            <h2 className="project-composer-title pnt-title">
              {t('pages.meetings.propose_new_time') || 'Propose a New Time'}
            </h2>
          </div>
          <button
            className="project-composer-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="project-composer-body pnt-body">
          {/* Current Meeting Time */}
          <div className="pnt-current-meeting">
            <label className="pnt-section-label">
              {t('pages.meetings.current_meeting_time') ||
                'Current meeting time:'}
            </label>
            <div className="pnt-meeting-time-display">
              <div className="pnt-meeting-time-item">
                <img src={calendarDotsIcon} alt="" className="pnt-meeting-icon" />
                <span className="pnt-meeting-time-value">
                  {formatMeetingDate()}
                </span>
              </div>
              <div className="pnt-meeting-time-item">
                <img src={clockIcon} alt="" className="pnt-meeting-icon" />
                <span className="pnt-meeting-time-value">
                  {formatMeetingStartTime()}
                </span>
              </div>
              <img src={arrowRightIcon} alt="" className="pnt-arrow-icon" />
              <span className="pnt-meeting-time-value">
                {formatMeetingEndTime()}
              </span>
            </div>
          </div>

          {/* Your Proposal Section */}
          <div className="pnt-proposal-section">
            <label className="pnt-section-label">
              {t('pages.meetings.your_proposal') || 'Your Proposal:'}
            </label>

            <div className="pnt-proposal-container">
              {/* Left Column: Date and Time */}
              <div className="pnt-proposal-left-column">
                <div className="pnt-field-group">
                  <label className="pnt-field-label">
                    {t('pages.calendar.schedule.start_date') || 'Start Date'}
                  </label>
                  <DatePickerInput
                    selected={formData.start_date}
                    onChange={handleStartDateChange}
                    minDate={new Date()}
                    isInvalid={!!errors.start_date}
                    placeholder={
                      t('pages.calendar.schedule.start_date_placeholder') ||
                      'Select date'
                    }
                  />
                  {errors.start_date && (
                    <div className="project-form-error">
                      {errors.start_date[0]}
                    </div>
                  )}
                </div>

                <div className="pnt-field-group">
                  <label className="pnt-field-label">
                    {t('pages.calendar.schedule.start_time') || 'Start Time'}
                  </label>
                  <TimePickerInput
                    selected={formData.start_time}
                    onChange={handleStartTimeChange}
                    isInvalid={!!errors.start_time}
                    placeholder={
                      t('pages.calendar.schedule.start_time_placeholder') ||
                      'Select time'
                    }
                  />
                  {errors.start_time && (
                    <div className="project-form-error">
                      {errors.start_time[0]}
                    </div>
                  )}
                </div>

                <div className="pnt-field-group">
                  <label className="pnt-field-label">
                    {t('pages.calendar.schedule.end_date') || 'End Date'}
                  </label>
                  <DatePickerInput
                    selected={formData.end_date}
                    onChange={handleEndDateChange}
                    minDate={formData.start_date || new Date()}
                    isInvalid={!!errors.end_date}
                    placeholder={
                      t('pages.calendar.schedule.end_date_placeholder') ||
                      'Select date'
                    }
                  />
                  {errors.end_date && (
                    <div className="project-form-error">{errors.end_date[0]}</div>
                  )}
                </div>

                <div className="pnt-field-group">
                  <label className="pnt-field-label">
                    {t('pages.calendar.schedule.end_time') || 'End Time'}
                  </label>
                  <TimePickerInput
                    selected={formData.end_time}
                    onChange={handleEndTimeChange}
                    isInvalid={!!errors.end_time}
                    placeholder={
                      t('pages.calendar.schedule.end_time_placeholder') ||
                      'Select time'
                    }
                  />
                  {errors.end_time && (
                    <div className="project-form-error">{errors.end_time[0]}</div>
                  )}
                </div>
              </div>

              {/* Right Column: Message */}
              <div className="pnt-proposal-right-column">
                <label className="pnt-field-label">
                  {t('pages.meetings.message_optional') || 'Message (Optional):'}
                </label>
                <textarea
                  className="pnt-message-textarea"
                  placeholder={
                    t('pages.meetings.message_placeholder') ||
                    'Add an optional message...'
                  }
                  value={formData.message}
                  onChange={handleMessageChange}
                />
              </div>
            </div>
          </div>

          {/* Find a Time Section */}
          <div className="pnt-find-time-section">
            <h3 className="pnt-find-time-title">
              {t('pages.meetings.find_a_time') || 'Find a Time'}
            </h3>
            {isLoadingAvailability ? (
              <MemberAvailabilityCalendarSkeleton />
            ) : (
              <MemberAvailabilityCalendar
                members={availabilityMembers}
                meetingStartDate={meeting?.start_at}
                calendarStartDate={availabilityAnchorDate || formData.start_date || meeting?.start_at}
                selectedDate={formData.start_date}
                selectedTime={formData.start_time}
                selectedEndDate={formData.end_date}
                selectedEndTime={formData.end_time}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="project-composer-footer">
          <button
            className="project-composer-btn project-composer-btn-secondary"
            onClick={onClose}
          >
            {t('common.actions.cancel') || 'Cancel'}
          </button>
          <button
            className="project-composer-btn project-composer-btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t('common.status.loading') || 'Loading...'
              : t('common.actions.save') || 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProposeNewTimeModal;
